import classNames from 'classnames'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { RefObject } from 'react'

import { SelectProps } from '../components/forms/select/select.js'
import { BaseSelectProps, SelectOptionProps } from '../components/forms/select/select.js'
import { Option } from '../components/types/common.js'

interface CommonParams {
    inputRef: RefObject<HTMLInputElement | null>
    search: string
    setSearch: (search: string) => void
    isOpen: boolean
    setOpen: (isOpen: boolean) => void
    highlightedIndex: number
    setHighlightedIndex: (index: number) => void
}

interface SingleSelectParams<T extends string> extends CommonParams, SelectProps<T> {}

interface MultiSelectParams<T extends string> extends CommonParams {
    className?: string
    wrapperClass?: string
    label?: string
    isRequired?: boolean
    isLoading?: boolean
    note?: BaseSelectProps['note']
    size?: BaseSelectProps['size']
    isDisabled?: boolean
    placeholder?: string
    inputClass?: string
    options: Option<T>[]
    value: T[]
    onChange: (value: T[]) => void
}

interface AddressInputParams extends Omit<CommonParams, 'inputRef'> {
    wrapperClass?: string
    isLoading?: boolean
    label: string
    options: Option[]
    selected: string
    onSelect: (option: Option) => void
}

export const getSingleSelectProps = <T extends string>(
    params: SingleSelectParams<T>,
): BaseSelectProps => {
    const searchLower = params.search.toLowerCase()
    const selectedLabel = params.options.find((option) => option.value === params.value)?.label

    const filteredOptions = params.options.filter((option) => {
        return !params.search || option.label.toLowerCase().includes(searchLower)
    })

    const closeDropdown = () => {
        params.setOpen(false)
        params.setSearch('')
        params.setHighlightedIndex(-1)
    }

    // Clicking the trigger while open should close it. The field is a <label>,
    // so a plain click would otherwise re-focus the input and reopen via onFocus.
    const onTriggerMouseDown = (event: React.MouseEvent) => {
        if (!params.isOpen) return
        event.preventDefault()
        closeDropdown()
        params.inputRef.current?.blur()
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!filteredOptions.length) return

        if (event.key === 'ArrowDown') {
            params.setHighlightedIndex((params.highlightedIndex + 1) % filteredOptions.length)
        } else if (event.key === 'ArrowUp') {
            if (params.highlightedIndex === -1) {
                params.setHighlightedIndex(filteredOptions.length - 1)
            } else {
                params.setHighlightedIndex(
                    (params.highlightedIndex - 1 + filteredOptions.length) % filteredOptions.length,
                )
            }
        } else if (event.key === 'Enter' || event.key === ' ') {
            if (params.highlightedIndex === -1) return
            params.onChange(filteredOptions[params.highlightedIndex].value)
            closeDropdown()
            params.inputRef.current?.blur()
        }
    }

    const props: BaseSelectProps = {
        className: classNames('select', params.className),
        wrapperClass: params.wrapperClass,
        onTriggerMouseDown,
        input: {
            ref: params.inputRef,
            className: classNames(
                'select__input',
                params.isDisabled && 'select__input--disabled',
                params.inputClass,
            ),
            type: 'text',
            value: params.isOpen ? params.search : (selectedLabel ?? ''),
            onChange: (event) => {
                params.setSearch(event.currentTarget.value)
                params.setHighlightedIndex(0)
            },
            onFocus: () => {
                params.setOpen(true)

                params.setHighlightedIndex(
                    filteredOptions.findIndex((option) => option.value === params.value),
                )
            },
            onBlur: closeDropdown,
            onKeyDown,
            placeholder: params.placeholder,
            disabled: params.isDisabled,
        },
        isLoading: params.isLoading,
        isDisabled: params.isDisabled,
        icon: params.isOpen ? ChevronUp : ChevronDown,
        note: params.note,
        size: params.size,
    }

    if (params.label) {
        props.label = params.label
        props.isRequired = params.isRequired
    }

    if (params.isOpen && filteredOptions.length) {
        props.options = {
            options: filteredOptions.map(
                (option, index): SelectOptionProps => ({
                    isSelected: option.value === params.value,
                    isHighlighted: index === params.highlightedIndex,
                    scroll: index === params.highlightedIndex,
                    label: option.label,
                    onMouseOver: () => params.setHighlightedIndex(index),
                    onMouseDown: () => {
                        params.onChange(option.value)
                        closeDropdown()
                    },
                }),
            ),
        }
    }

    return props
}

export const getMultiSelectProps = <T extends string>(
    params: MultiSelectParams<T>,
): BaseSelectProps => {
    const searchLower = params.search.toLowerCase()
    const selected = new Set(params.value)

    const filteredOptions = params.options.filter((option) => {
        return !params.search || option.label.toLowerCase().includes(searchLower)
    })

    const closeDropdown = () => {
        params.setOpen(false)
        params.setSearch('')
        params.setHighlightedIndex(-1)
    }

    // Clicking the trigger while open should close it (see single-select note).
    const onTriggerMouseDown = (event: React.MouseEvent) => {
        if (!params.isOpen) return
        event.preventDefault()
        closeDropdown()
        params.inputRef.current?.blur()
    }

    const toggleValue = (value: T) => {
        const next = selected.has(value)
            ? params.value.filter((v) => v !== value)
            : [...params.value, value]

        params.onChange(next)
    }

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!filteredOptions.length) return

        if (event.key === 'ArrowDown') {
            params.setHighlightedIndex((params.highlightedIndex + 1) % filteredOptions.length)
        } else if (event.key === 'ArrowUp') {
            if (params.highlightedIndex === -1) {
                params.setHighlightedIndex(filteredOptions.length - 1)
            } else {
                params.setHighlightedIndex(
                    (params.highlightedIndex - 1 + filteredOptions.length) % filteredOptions.length,
                )
            }
        } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            if (params.highlightedIndex === -1) return

            toggleValue(filteredOptions[params.highlightedIndex].value)
            params.setSearch('')
            params.setHighlightedIndex(0)
            params.inputRef.current?.focus()
        }
    }

    const props: BaseSelectProps = {
        className: classNames('select', params.className),
        wrapperClass: params.wrapperClass,
        onTriggerMouseDown,
        chips: params.value
            .map((value) => {
                const label = params.options.find((option) => option.value === value)?.label
                if (!label) return undefined

                return {
                    label,
                    onRemove: () => {
                        params.onChange(params.value.filter((v) => v !== value))
                    },
                }
            })
            .filter((chip): chip is NonNullable<typeof chip> => Boolean(chip)),
        input: {
            ref: params.inputRef,
            className: classNames(
                'select__input',
                params.isDisabled && 'select__input--disabled',
                params.inputClass,
            ),
            type: 'text',
            value: params.isOpen ? params.search : '',
            onChange: (event) => {
                params.setSearch(event.currentTarget.value)
                params.setHighlightedIndex(0)
            },
            onFocus: () => {
                params.setOpen(true)
                params.setHighlightedIndex(0)
            },
            onBlur: closeDropdown,
            onKeyDown,
            placeholder: params.placeholder,
            disabled: params.isDisabled,
        },
        isLoading: params.isLoading,
        isDisabled: params.isDisabled,
        icon: params.isOpen ? ChevronUp : ChevronDown,
        note: params.note,
        size: params.size,
    }

    if (params.label) {
        props.label = params.label
        props.isRequired = params.isRequired
    }

    if (params.isOpen && filteredOptions.length) {
        props.options = {
            options: filteredOptions.map(
                (option, index): SelectOptionProps => ({
                    isSelected: selected.has(option.value),
                    isHighlighted: index === params.highlightedIndex,
                    scroll: index === params.highlightedIndex,
                    label: option.label,
                    onMouseOver: () => params.setHighlightedIndex(index),
                    onMouseDown: (event) => {
                        event.preventDefault()
                        toggleValue(option.value)
                        params.setSearch('')
                        params.setHighlightedIndex(0)
                        params.inputRef.current?.focus()
                    },
                }),
            ),
        }
    }

    return props
}

export const getAddressInputProps = (params: AddressInputParams): BaseSelectProps => {
    const closeDropdown = () => {
        params.setOpen(false)
        params.setHighlightedIndex(-1)
    }

    const count = params.options.length

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!count) return

        if (event.key === 'ArrowDown') {
            params.setHighlightedIndex((params.highlightedIndex + 1) % count)
        } else if (event.key === 'ArrowUp') {
            if (params.highlightedIndex === -1) {
                params.setHighlightedIndex(count - 1)
            } else {
                params.setHighlightedIndex((params.highlightedIndex - 1 + count) % count)
            }
        } else if (event.key === 'Enter') {
            params.onSelect(params.options[params.highlightedIndex])
            closeDropdown()
        } else if (event.key === 'Escape') {
            closeDropdown()
        }
    }

    const props: BaseSelectProps = {
        className: 'select',
        wrapperClass: params.wrapperClass,
        input: {
            className: classNames('select__input'),
            type: 'text',
            value: params.search,
            onChange: (event) => {
                params.setSearch(event.currentTarget.value)
                params.setHighlightedIndex(0)
            },
            onFocus: () => {
                params.setOpen(true)

                params.setHighlightedIndex(
                    params.options.findIndex((option) => option.value === params.selected),
                )
            },
            onBlur: closeDropdown,
            onKeyDown,
        },
        isLoading: params.isLoading,
        label: params.label,
    }

    if (params.isOpen && count) {
        props.options = {
            options: params.options.map(
                (option, index): SelectOptionProps => ({
                    isSelected: option.value === params.selected,
                    isHighlighted: index === params.highlightedIndex,
                    scroll: index === params.highlightedIndex,
                    label: option.label,
                    onMouseOver: () => params.setHighlightedIndex(index),
                    onMouseDown: () => {
                        params.onSelect(option)
                        closeDropdown()
                    },
                }),
            ),
        }
    }

    return props
}
